import { FC, useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import ChatRounded from '@mui/icons-material/ChatRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import robot from '../../../../../../assets/robot.png'
import { signupAssistantMessages, AssistantMessage } from './assistantMessages'
import './SignUpAssistant.css'

interface SignUpAssistantProps {
  currentStep: number
}

const TYPING_DELAY_MS = 600
const MESSAGE_INTERVAL_MS = 1200

export const SignUpAssistant: FC<SignUpAssistantProps> = ({ currentStep }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [visibleMessages, setVisibleMessages] = useState<number>(0)
  const [isTyping, setIsTyping] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const prevStepRef = useRef<number>(currentStep)

  const stepData = signupAssistantMessages[currentStep]

  // Reset messages when step changes
  useEffect(() => {
    if (prevStepRef.current !== currentStep) {
      setVisibleMessages(0)
      setIsTyping(false)
      prevStepRef.current = currentStep
    }
  }, [currentStep])

  // Sequential message reveal with typing effect
  useEffect(() => {
    if (!isOpen || !stepData) return

    const totalMessages = stepData.messages.length + 1 // +1 for greeting

    if (visibleMessages >= totalMessages) {
      setIsTyping(false)
      return
    }

    const typingTimer = setTimeout(() => {
      setIsTyping(true)
    }, visibleMessages === 0 ? 300 : MESSAGE_INTERVAL_MS)

    const messageTimer = setTimeout(
      () => {
        setIsTyping(false)
        setVisibleMessages((prev) => prev + 1)
      },
      visibleMessages === 0 ? 300 + TYPING_DELAY_MS : MESSAGE_INTERVAL_MS + TYPING_DELAY_MS
    )

    return () => {
      clearTimeout(typingTimer)
      clearTimeout(messageTimer)
    }
  }, [isOpen, visibleMessages, stepData])

  // Auto-scroll to latest message
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [visibleMessages, isTyping])

  // Auto-open on first render after a short delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsOpen(true)
    }, 1500)
    return () => clearTimeout(timer)
  }, [])

  const handleToggle = useCallback(() => {
    if (isOpen) {
      setIsClosing(true)
      setTimeout(() => {
        setIsOpen(false)
        setIsClosing(false)
      }, 200)
    } else {
      setIsOpen(true)
      setVisibleMessages(0)
      setIsTyping(false)
    }
  }, [isOpen])

  if (!stepData) return null

  const allMessages: { emoji: string; text: string; isGreeting: boolean }[] = [
    { emoji: '', text: stepData.greeting, isGreeting: true },
    ...stepData.messages.map((msg: AssistantMessage) => ({
      ...msg,
      isGreeting: false,
    })),
  ]

  return createPortal(
    <>
      {/* Floating Button — same style as AssistantPet */}
      <div className='signup-assistant-trigger'>
        <button
          type='button'
          onClick={handleToggle}
          className='inline-flex h-12 items-center gap-2 rounded-full border border-white/90 bg-white px-4 text-sm font-semibold text-primary shadow-[0_18px_34px_rgba(15,23,42,0.14)] transition hover:-translate-y-0.5 hover:bg-slate-50 sm:h-14 sm:px-5'
          aria-label={isOpen ? 'Cerrar asistente' : 'Abrir asistente de registro'}
          title={isOpen ? 'Cerrar asistente' : 'Asistente de registro'}
        >
          <span className='inline-flex h-9 w-9 items-center justify-center rounded-full text-white shadow-[0_10px_20px_rgba(15,23,42,0.18)]' style={{ backgroundColor: '#000033' }}>
            <ChatRounded sx={{ fontSize: 18 }} />
          </span>
          <span>{isOpen ? 'Cerrar asistente' : 'Asistente de registro'}</span>
        </button>

        {/* Robot Avatar */}
        <span className='signup-assistant-robot'>
          <span className='absolute inset-2 rounded-full bg-primary/15 blur-xl' aria-hidden='true' />
          <img
            src={robot}
            alt='Luca Asistente'
            draggable={false}
            className='relative h-20 w-20 object-contain sm:h-16 sm:w-16'
          />
        </span>
      </div>

      {/* Chat Panel — same style as AssistantPetChatPanel */}
      {(isOpen || isClosing) && (
        <div className={`signup-assistant-panel ${isClosing ? 'is-closing' : ''}`}>
          <section className='pointer-events-auto w-[min(calc(100vw-1.5rem),22rem)] rounded-[32px] border border-slate-200/80 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.18)]'>
            {/* Header */}
            <div className='flex items-center gap-2.5 rounded-t-[32px] px-3.5 py-2.5 text-white' style={{ backgroundColor: '#000033' }}>
              <div className='flex h-8 w-8 items-center justify-center rounded-full border border-white/30 bg-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.3)]'>
                <img src={robot} alt='LUCA' className='h-5 w-5 object-contain' draggable={false} />
              </div>
              <div className='min-w-0 flex-1'>
                <div className='text-sm font-semibold tracking-[0.06em]'>Asistente de Registro</div>
                <div className='signup-assistant-status'>
                  <span className='signup-assistant-status-dot' />
                  <span className='text-[11px] text-white/80'>Guiándote paso a paso</span>
                </div>
              </div>
              <button
                type='button'
                onClick={handleToggle}
                className='inline-flex h-8 w-8 items-center justify-center rounded-full text-white/80 transition hover:bg-white/10 hover:text-white'
                aria-label='Cerrar asistente'
                title='Cerrar'
              >
                <CloseRoundedIcon sx={{ fontSize: 18 }} />
              </button>
            </div>

            {/* Messages */}
            <div className='signup-assistant-messages bg-gradient-to-b from-white via-white to-slate-50 px-2.5 py-2'>
              <div className='flex min-h-full flex-col gap-1.5'>
                {allMessages.slice(0, visibleMessages).map((msg, index) => (
                  <div
                    key={`${currentStep}-${index}`}
                    className={`signup-assistant-msg ${msg.isGreeting ? 'is-greeting' : ''}`}
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <span className='signup-assistant-msg-emoji'>
                      {msg.isGreeting ? '🤖' : msg.emoji}
                    </span>
                    <span className={`signup-assistant-msg-text ${msg.isGreeting ? 'bg-primary/5 border-primary/15 font-medium' : 'bg-slate-50 border-slate-100'}`}>
                      {msg.text}
                    </span>
                  </div>
                ))}

                {/* Typing indicator */}
                {isTyping && (
                  <div className='signup-assistant-typing'>
                    <div className='signup-assistant-typing-dot' />
                    <div className='signup-assistant-typing-dot' />
                    <div className='signup-assistant-typing-dot' />
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Footer step indicator */}
            <div className='border-t border-slate-200/80 bg-white px-3 py-2.5 rounded-b-[32px]'>
              <div className='flex items-center justify-center gap-1.5'>
                {[0, 1, 2, 3].map((step) => (
                  <div
                    key={step}
                    className={`signup-assistant-step-dot ${step === currentStep ? 'active' : ''}`}
                  />
                ))}
                <span className='ml-2 text-[11px] text-slate-400'>
                  Paso {currentStep + 1} de 4
                </span>
              </div>
            </div>
          </section>
        </div>
      )}
    </>,
    document.body
  )
}
